import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import SettingsRowShell from './SettingsRowShell';

const EmbeddingsRow = ({
  embeddingStats,
  embeddingCoverage,
  selectedModel,
  canEdit,
  populatingEmbeddings,
  embeddingProgress,
  onPopulate,
  onRefresh,
}) => {
  const coverageColor = embeddingCoverage >= 80 ? 'success' : embeddingCoverage >= 50 ? 'warning' : 'error';

  return (
    <SettingsRowShell
      icon={<StorageIcon />}
      title="Embedding Statistics"
      description="Vector embedding coverage for similarity analysis"
      status={embeddingStats ? { label: `${embeddingCoverage}% Coverage`, color: coverageColor } : null}
      actions={
        <Tooltip title="Refresh statistics">
          <IconButton onClick={onRefresh} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      {embeddingStats ? (
        <>
          {/* Progress bar */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Embedding Coverage</Typography>
              <Typography variant="caption" fontWeight="bold" color="primary">
                {embeddingCoverage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={embeddingCoverage}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': { borderRadius: 4 },
              }}
            />
          </Box>

          {/* Stats boxes */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total', value: embeddingStats.total_test_cases, bg: 'primary.50', fg: 'primary.main', border: 'primary.100' },
              { label: 'With Embeddings', value: embeddingStats.with_embeddings, bg: 'success.50', fg: 'success.main', border: 'success.100' },
              { label: 'Missing', value: embeddingStats.without_embeddings, bg: 'warning.50', fg: 'warning.main', border: 'warning.100' },
            ].map((s) => (
              <Grid item xs={4} key={s.label}>
                <Paper
                  elevation={0}
                  sx={{ p: 1.5, textAlign: 'center', bgcolor: s.bg, border: '1px solid', borderColor: s.border, borderRadius: 1.5 }}
                >
                  <Typography variant="h5" fontWeight="bold" color={s.fg}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* By-model table */}
          {Object.keys(embeddingStats.by_model || {}).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                Embeddings by Model
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Model</TableCell>
                      <TableCell align="right">Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(embeddingStats.by_model).map(([model, count]) => (
                      <TableRow key={model}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {model}
                            {model === selectedModel && <Chip label="Current" size="small" color="primary" />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {embeddingStats.mismatched_count > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{embeddingStats.mismatched_count}</strong> test cases have embeddings from a different model. Regenerate for consistent results.
            </Alert>
          )}

          {/* Actions */}
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(embeddingStats.without_embeddings > 0 || embeddingStats.mismatched_count > 0) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={populatingEmbeddings ? <CircularProgress size={16} /> : <PsychologyIcon />}
                  onClick={() => onPopulate(false)}
                  disabled={populatingEmbeddings}
                >
                  {populatingEmbeddings
                    ? 'Generating…'
                    : `Generate Missing (${embeddingStats.without_embeddings + embeddingStats.mismatched_count})`}
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={populatingEmbeddings ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => onPopulate(true)}
                disabled={populatingEmbeddings}
              >
                {populatingEmbeddings
                  ? 'Regenerating…'
                  : `Regenerate All (${embeddingStats.total_test_cases})`}
              </Button>
            </Box>
          )}

          {/* Progress */}
          {populatingEmbeddings && embeddingProgress && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {embeddingProgress.message || 'Processing embeddings…'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={embeddingProgress.progress_percent || 0}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
                <Typography variant="caption" fontWeight="bold">
                  {Math.round(embeddingProgress.progress_percent || 0)}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {embeddingProgress.processed || 0} / {embeddingProgress.total || 0} processed
                {embeddingProgress.errors > 0 && (
                  <span style={{ color: 'red', marginLeft: 8 }}>
                    ({embeddingProgress.errors} errors)
                  </span>
                )}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Alert severity="info">No embedding statistics available.</Alert>
      )}
    </SettingsRowShell>
  );
};

export default EmbeddingsRow;
